import { z } from "zod";
import { instanceScreenUrl } from "../instanceAddress";

/**
 * The MCP write scope's prepare-then-commit intents and the approve link (ADR 0009 section 6,
 * release 8B). One place for the numbers, the names and the HTTP shapes that the store
 * (`server/auth/mcp/writeIntents.ts`), the write tools (`server/mcp/`), the approve routes
 * (`/api/v2/mcp/write-intents/*`), the approve page and the runner's sweep (`./sweep.ts`) share.
 * The migration `mcp_write_intents` spells the status and reason lists again in its CHECKs;
 * `shared/mcp/__tests__/writeIntents.test.ts` keeps the two in step.
 *
 * The owner's rule holds here as everywhere in MCP: no credential is ever sent by the AI app or
 * returned to it. An intent's arguments are refused before the row is written if any field id is
 * a credential or any value looks like a secret (`MCP_CREDENTIAL_REFUSED`, naming the field id
 * only, never the value). A connection made through MCP is saved as a draft; the person types the
 * credential in skyData's own form (`/connections/:id/setup`), runs Connect and saves.
 *
 * Nothing here imports Node: the client bundles this file.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** A prepare id is good for 5 minutes, single use, bound to the grant, the tool and the arguments. */
export const MCP_WRITE_PREPARE_TTL_MS = 5 * MINUTE_MS;
/** The approve link lives 24 hours (owner, 2026-10-05); then the intent is `expired`. */
export const MCP_WRITE_APPROVAL_TTL_MS = DAY_MS;
/** Every write beyond this many in an hour on one grant needs the approve link. */
export const MCP_WRITES_PER_HOUR = 10;
/**
 * Approve links one grant may hold open at once. A prepare past it ends the grant's oldest link
 * (`expired`), so a model cannot pile up unbounded links and rows (round 8 review F6).
 */
export const MCP_OPEN_APPROVALS_MAX = 25;
/** The same bound for prepared ids not yet committed (valid 5 minutes each). */
export const MCP_OPEN_PREPARES_MAX = 50;
/** A write's arguments, as JSON, may not be larger than this; the real ones are a few KB. */
export const MCP_WRITE_ARGS_MAX_BYTES = 64 * 1024;
/** The window the hourly limit counts over. */
export const MCP_WRITE_WINDOW_MS = HOUR_MS;
/** A write covering MORE than this many tables needs the approve link; exactly 20 does not. */
export const MCP_APPROVE_TABLE_THRESHOLD = 20;
/** A write still `committing` or `executing` after this long is marked `failed` by the sweep. */
export const MCP_WRITE_STUCK_MS = 10 * MINUTE_MS;
/** An ended intent (executed, failed, declined, expired) is deleted this long after it was prepared. */
export const MCP_ENDED_INTENT_KEEP_MS = 30 * DAY_MS;

/**
 * The write tools of ADR 0009 section 6 (with the owner's 2026-10-05 decisions). The store does
 * not refuse another name (the column is a plain identifier), so the tool registry stays the one
 * list; this one is for labels and tests.
 */
export const MCP_WRITE_TOOL_NAMES = [
  "create_connection",
  "update_connection_label",
  "revalidate_connection",
  "select_entities",
  "enable_connection",
  "disable_connection",
  "create_schedule",
  "update_schedule",
  "pause_schedule",
  "resume_schedule",
  "start_run",
  "retry_run",
  "retry_tables",
  "run_dbt",
] as const;
export type McpWriteToolName = (typeof MCP_WRITE_TOOL_NAMES)[number];

/** What the approve page calls each write, a fixed sentence per tool. */
export const MCP_WRITE_TOOL_LABELS: Record<McpWriteToolName, string> = {
  create_connection: "Create a connection (saved as a draft, without credentials)",
  update_connection_label: "Rename a connection or change its description",
  revalidate_connection: "Test a connection with its stored credentials",
  select_entities: "Change the tables a connection imports",
  enable_connection: "Enable a connection",
  disable_connection: "Disable a connection",
  create_schedule: "Create a schedule",
  update_schedule: "Change a schedule",
  pause_schedule: "Pause a schedule",
  resume_schedule: "Resume a schedule",
  start_run: "Start a run",
  retry_run: "Retry a run",
  retry_tables: "Retry tables of a run",
  run_dbt: "Run dbt transformations",
};

/**
 * Write tools that are gone but whose stored `mcp_write_intents` rows still render (mirroring
 * `HISTORICAL_AUDIT_ACTIONS`): `refresh_explorer` was removed with the Explorer (ADR 0011,
 * 2026-10-08). No tool of this name is listed or dispatched.
 */
export const HISTORICAL_MCP_WRITE_TOOL_LABELS: Readonly<Record<string, string>> = {
  refresh_explorer: "Refresh the Explorer's warehouse catalog (no longer available)",
};

export function isMcpWriteToolName(value: string): value is McpWriteToolName {
  return (MCP_WRITE_TOOL_NAMES as readonly string[]).includes(value);
}

/** A stored intent's words: the tool's label, a removed tool's historical one, or its name. */
export function mcpWriteToolLabel(tool: string): string {
  if (isMcpWriteToolName(tool)) return MCP_WRITE_TOOL_LABELS[tool];
  return Object.hasOwn(HISTORICAL_MCP_WRITE_TOOL_LABELS, tool)
    ? HISTORICAL_MCP_WRITE_TOOL_LABELS[tool]!
    : tool;
}

/**
 * An intent's life:
 *
 * - `prepared` → `committing` (the commit claims it, exactly once) → `executed` or `failed`;
 * - `committing` → `awaiting_approval` (the write needs the link) → `executing` (the person
 *   approved; the write runs then, in process, under the grant) → `executed` or `failed`;
 * - `awaiting_approval` → `declined` (the person declined) or `expired` (24 hours passed);
 * - `prepared` → `expired` (never committed within 5 minutes; the sweep marks it).
 *
 * The migration's `mcp_write_intents_status_check` lists the same words.
 */
export const MCP_WRITE_INTENT_STATUSES = [
  "prepared",
  "committing",
  "awaiting_approval",
  "executing",
  "executed",
  "failed",
  "declined",
  "expired",
] as const;
export type McpWriteIntentStatus = (typeof MCP_WRITE_INTENT_STATUSES)[number];

/** The statuses nothing moves on from. */
export const MCP_WRITE_INTENT_ENDED_STATUSES = [
  "executed",
  "failed",
  "declined",
  "expired",
] as const satisfies readonly McpWriteIntentStatus[];

export function isEndedWriteIntentStatus(status: McpWriteIntentStatus): boolean {
  return (MCP_WRITE_INTENT_ENDED_STATUSES as readonly string[]).includes(status);
}

/**
 * Why a write needs the approve link (ADR 0009 section 6): enabling or disabling a connection,
 * always; a run (or a schedule's firings, or an entity selection on a scheduled connection)
 * covering more than 20 tables; every write beyond 10 in an hour on one grant. The migration's
 * `mcp_write_intents_reason_check` lists the same words.
 */
export const MCP_WRITE_APPROVAL_REASONS = [
  "enable_disable",
  "tables_over_20",
  "hourly_limit",
] as const;
export type McpWriteApprovalReason = (typeof MCP_WRITE_APPROVAL_REASONS)[number];

export const MCP_WRITE_APPROVAL_REASON_LABELS: Record<McpWriteApprovalReason, string> = {
  enable_disable: "Enabling or disabling a connection always needs your approval.",
  tables_over_20: `This write covers more than ${MCP_APPROVE_TABLE_THRESHOLD} tables.`,
  hourly_limit: `This AI app has already made ${MCP_WRITES_PER_HOUR} writes in the last hour.`,
};

/** How the tables a write covers were counted, shown beside the number. */
export const MCP_WRITE_COVERAGE_BASES = [
  "none",
  "entity_names",
  "selected_tables",
  "schedule_members",
  "run_scope",
  "retryable_tables",
  "projected_selection",
] as const;
export type McpWriteCoverageBasis = (typeof MCP_WRITE_COVERAGE_BASES)[number];

export interface McpWriteCoverage {
  tables: number;
  basis: McpWriteCoverageBasis;
}

/**
 * What the approve page shows about a write: identifiers, names already scrubbed by the tool,
 * and counts. Never a credential, never a customer data value. `value` is a scalar or a short
 * list; the page renders it as text.
 */
export interface McpWriteSummaryItem {
  /** A stable key the page may label ("connection", "schedule", "tables", "fullRefresh"). */
  key: string;
  label: string;
  value: string | number | boolean | null | string[];
}

export interface McpWriteSummary {
  items: McpWriteSummaryItem[];
}

export const mcpWriteSummarySchema = z
  .object({
    items: z
      .array(
        z
          .object({
            key: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/),
            label: z.string().min(1).max(120),
            value: z.union([
              z.string().max(500),
              z.number(),
              z.boolean(),
              z.null(),
              z.array(z.string().max(200)).max(50),
            ]),
          })
          .strict(),
      )
      .max(30),
  })
  .strict();

/** A prepare id and approve-link id: 22 base64url characters (128 random bits). */
export const MCP_WRITE_INTENT_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;

export function isMcpWriteIntentId(value: string): boolean {
  return MCP_WRITE_INTENT_ID_PATTERN.test(value);
}

/**
 * The SPA route of the approve page. The link is `<PUBLIC_APP_URL>/i/<instance>/mcp-approve/<id>`
 * since round 9 (T-58); one sent before, `/mcp-approve/<id>?instance=<id>`, still opens it.
 */
export const MCP_APPROVE_PATH = "/mcp-approve";

/** The approve link, built by the server from `PUBLIC_APP_URL` only (never the request's `Host`). */
export function mcpApproveUrl(origin: string, intentId: string, instanceId: string): string {
  return instanceScreenUrl(
    origin,
    instanceId,
    `${MCP_APPROVE_PATH}/${encodeURIComponent(intentId)}`,
  );
}

/**
 * The setup link a draft connection made through MCP answers: the person types the credential
 * here, in skyData, runs Connect and saves. Only the instance id rides in the address (`/i/<id>/`, round 9 T-58).
 */
export function mcpConnectionSetupUrl(
  origin: string,
  connectionId: string,
  instanceId: string,
): string {
  return instanceScreenUrl(
    origin,
    instanceId,
    `/connections/${encodeURIComponent(connectionId)}/setup`,
  );
}

// ---------------------------------------------------------------------------------------------
// The approve page's API: `GET /api/v2/mcp/write-intents/:id`, `POST …/:id/approve`,
// `POST …/:id/decline` (signed in, `x-instance-id` the grant's instance, `instance.read`; only the
// grant's person sees or decides an intent, everyone else gets 404).
// ---------------------------------------------------------------------------------------------

export const mcpWriteIntentParamsSchema = z.object({
  id: z.string().regex(MCP_WRITE_INTENT_ID_PATTERN),
});

/** Both decisions take an empty body: the id in the path is the whole request. */
export const mcpWriteIntentApproveBodySchema = z.object({}).strict();
export const mcpWriteIntentDeclineBodySchema = z.object({}).strict();

/** The recorded outcome of a write that ran (or could not). */
export interface McpWriteOutcome {
  /** An `ErrorCode` when the write failed (`MCP_GRANT_ENDED`, a route's refusal code), else null. */
  code: string | null;
  /** The tool's picked result fields, when it ran; never a credential. */
  result: unknown;
}

/** What the approve page shows. */
export interface McpWriteIntentView {
  id: string;
  tool: string;
  /** `MCP_WRITE_TOOL_LABELS[tool]`, or the tool name for one the list does not know. */
  toolLabel: string;
  status: McpWriteIntentStatus;
  reason: McpWriteApprovalReason | null;
  summary: McpWriteSummary;
  /**
   * The arguments as the AI app sent them (credential-free by construction), shown verbatim so the
   * person approves exactly what runs. Empty (`{}`) once the intent has ended: the store clears
   * them when it executes, fails, is declined or expires; `summary` stays.
   */
  args: Record<string, unknown>;
  coverage: McpWriteCoverage | null;
  /** "Claude, <instance>": the grant the write runs under. */
  grantName: string;
  /** The client's display name from `MCP_REDIRECT_URIS`, or "AI app". */
  clientName: string;
  instanceId: string;
  preparedAt: string;
  approvalExpiresAt: string | null;
  approvedAt: string | null;
  endedAt: string | null;
  outcome: McpWriteOutcome | null;
}

/** What approve and decline answer: the intent as it now stands. */
export interface McpWriteIntentDecisionResult {
  intent: McpWriteIntentView;
}
